import { InvalidCredentialsError } from "../../../domain/errors/invalid-credentials-error";
import { UnexpectedError } from "../../../domain/errors/unexpected-error";
import {
  EconomicActivity,
  EconomicActivityModel,
  EconomicActivityResponseModel,
} from "../../../domain/models/economic-activity";
import { FilterModel } from "../../../domain/models/enum";
import { HttpClient, HttpStatusCode } from "../../protocols/http/http-client";

export class RemoteEconomicActivity implements EconomicActivity {
  constructor(
    private readonly url: string,
    private readonly httpPostClient: HttpClient
  ) {}

  async load(params?: FilterModel): Promise<EconomicActivityResponseModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}`,
      method: "get",
      params,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async create(body: EconomicActivityModel): Promise<EconomicActivityModel> {
    const httpResponse = await this.httpPostClient.request({
      url: this.url,
      method: "post",
      body,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.created:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.conflict:
        throw httpResponse.body;
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async delete(id: number): Promise<EconomicActivityModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "delete",
    });
    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.conflict:
        throw httpResponse.body;
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async loadById(id: number): Promise<EconomicActivityModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async loadByCNAE(id: number): Promise<EconomicActivityModel[]> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/cnae/${id}`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async update(
    id: number | undefined,
    body: EconomicActivityModel
  ): Promise<EconomicActivityModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "put",
      body,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async loadAll(): Promise<EconomicActivityModel[]> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/all`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }
}
